import React, { useState, useEffect, useRef } from "react";
import { useFetchSchemeProductsCodesMutation } from "../../features/tenant/scheme/schemeApi"; //"../../../../features/tenant/scheme/schemeApi";
import { getToken, getTenantId } from "./helpers";

// ── MultiSelect ───────────────────────────────────────────────────────────────
export const MultiSelect = ({
  label,
  options = [],
  selected = [],
  onChange,
  loading,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = (val) =>
    onChange(
      selected.includes(val)
        ? selected.filter((v) => v !== val)
        : [...selected, val],
    );

  const displayText =
    selected.length === 0
      ? `Select ${label}...`
      : selected.length === 1
        ? selected[0]
        : `${selected.length} selected`;

  const filtered = options.filter((opt) =>
    (opt.label ?? opt).toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="relative" ref={ref}>
      <label className="block text-xs font-semibold text-gray-500 mb-1">
        {label}
      </label>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={loading}
        className="w-full text-left border border-gray-300 rounded-lg px-3 py-2.5 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-400 flex justify-between items-center disabled:opacity-50"
      >
        <span className="truncate">{loading ? "Loading..." : displayText}</span>
        <svg
          className={`w-4 h-4 text-gray-400 ml-2 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-xl max-h-64 overflow-hidden flex flex-col">
          <div className="p-2 border-b">
            <input
              type="text"
              placeholder={`Search ${label}...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
          </div>
          <div className="flex justify-end px-3 py-1 border-b bg-gray-50">
            <button
              onClick={() => {
                setOpen(false);
                setSearch("");
              }}
              className="text-xs font-semibold text-blue-600 hover:underline"
            >
              Done
            </button>
          </div>
          <div className="overflow-y-auto max-h-48">
            {filtered.length === 0 ? (
              <p className="px-3 py-2 text-xs text-gray-400">
                No results found
              </p>
            ) : (
              filtered.map((opt) => {
                const value = opt.value ?? opt;
                const lbl = opt.label ?? opt;
                return (
                  <label
                    key={value}
                    className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm text-gray-700"
                  >
                    <input
                      type="checkbox"
                      checked={selected.includes(value)}
                      onChange={() => toggle(value)}
                      className="rounded"
                    />
                    {lbl}
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ── SingleSelect ──────────────────────────────────────────────────────────────
export const SingleSelect = ({
  label,
  options = [],
  value,
  onChange,
  placeholder = "Select...",
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selected = options.find((o) => o.value === value);

  return (
    <div className="relative" ref={ref}>
      <label className="block text-xs font-semibold text-gray-500 mb-1">
        {label}
      </label>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full text-left border border-gray-300 rounded-lg px-3 py-2.5 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-400 flex justify-between items-center"
      >
        <span className="truncate">
          {selected ? selected.label : placeholder}
        </span>
        <svg
          className={`w-4 h-4 text-gray-400 ml-2 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden">
          <button
            onClick={() => {
              onChange("");
              setOpen(false);
            }}
            className="w-full text-left px-3 py-2 text-xs text-gray-400 hover:bg-gray-50 border-b border-gray-100"
          >
            — Clear selection
          </button>
          {options.map((opt) => (
            <button
              key={opt.value}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2 ${value === opt.value ? "font-semibold text-blue-600 bg-blue-50" : "text-gray-700"}`}
            >
              {value === opt.value ? (
                <svg
                  className="w-3.5 h-3.5 shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : (
                <span className="w-3.5 shrink-0" />
              )}
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ── ProductCodeSearchSelect ───────────────────────────────────────────────────
export const ProductCodeSearchSelect = ({
  schemeId,
  selected = [],
  onChange,
}) => {
  const [searchInput, setSearchInput] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const debounceRef = useRef(null);
  const [
    fetchProductCodes,
    { data: productCodeData, isLoading: productCodeLoading },
  ] = useFetchSchemeProductsCodesMutation();

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (!searchInput.trim()) {
      setOpen(false);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchProductCodes({
        token: getToken(),
        tenant_id: getTenantId(),
        body: { scheme_id: schemeId, product_codes: [searchInput.trim()] },
      });
      setOpen(true);
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput, schemeId]);

  const results = productCodeData?.body ?? [];
  const toggle = (code) =>
    onChange(
      selected.includes(code)
        ? selected.filter((c) => c !== code)
        : [...selected, code],
    );

  return (
    <div className="relative" ref={ref}>
      <label className="block text-xs font-semibold text-gray-500 mb-1">
        Product Codes
      </label>
      <div className="flex items-center border border-gray-300 rounded-lg bg-white focus-within:ring-2 focus-within:ring-blue-400 overflow-hidden">
        <input
          type="text"
          placeholder="Type a product code to search..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onFocus={() => {
            if (results.length > 0) setOpen(true);
          }}
          className="flex-1 px-3 py-2.5 text-sm text-gray-700 bg-transparent focus:outline-none"
        />
        {productCodeLoading && (
          <span className="px-3 text-xs text-gray-400">Searching...</span>
        )}
      </div>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {selected.map((code) => (
            <span
              key={code}
              className="inline-flex items-center gap-1 bg-blue-50 border border-blue-200 text-blue-700 text-xs font-medium px-2 py-0.5 rounded-full"
            >
              {code}
              <button
                onClick={() => onChange(selected.filter((c) => c !== code))}
                className="text-blue-400 hover:text-blue-700 font-bold leading-none"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      {open && results.length > 0 && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-xl">
          <div className="flex justify-end px-3 py-1 border-b bg-gray-50">
            <button
              onClick={() => setOpen(false)}
              className="text-xs font-semibold text-blue-600 hover:underline"
            >
              Done
            </button>
          </div>
          <div className="max-h-48 overflow-y-auto">
            {results.map((code) => (
              <label
                key={code}
                className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm text-gray-700"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(code)}
                  onChange={() => toggle(code)}
                  className="rounded"
                />
                {code}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
